import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ws } from "@/lib/ws";

/**
 * CliLoginPage — the browser half of `pure login`'s OAuth-style flow
 * (internal/cli's login.ts), same shape as Claude Code's own CLI login:
 * the CLI starts a local HTTP server on a random port and opens this page
 * with `?state=<nonce>&port=<port>`; after the user approves here, we
 * mint a fresh personal API key (keys.createForCli — see that handler's
 * own comment for why this is a separate, non-admin-gated path from the
 * dashboard's regular key management) and hand it to the CLI in ONE of
 * two ways:
 *   1. A same-machine redirect to `http://127.0.0.1:<port>/callback` —
 *      works whenever the CLI's local server is actually reachable from
 *      this browser (the overwhelmingly common case: same laptop).
 *   2. The raw key is ALSO always shown on-screen with a copy button and
 *      `pure login --paste` instructions — the fallback for SSH/remote/
 *      container sessions where the browser can't reach localhost on the
 *      machine actually running `pure`, same "shows a code instead of
 *      redirecting" fallback Claude Code's own docs describe. We can't
 *      reliably detect from here whether the redirect actually worked
 *      (a failed loopback connection from the browser raises no
 *      JS-visible error for a top-level navigation), so both are offered
 *      unconditionally rather than guessing.
 *
 * Not nested under AppShell (unlike every other authenticated page) —
 * AppShell's own unauthenticated-redirect drops the current path
 * entirely (`navigate('/login')`, no return param), which would lose
 * state/port. This page manages its own auth check instead, preserving
 * the full URL via AuthPage's `?return=`.
 */
export function CliLoginPage() {
  const [params] = useSearchParams();
  const state = params.get("state");
  const port = params.get("port");
  // Chosen by the user in the CLI's own prompt before it ever opened this
  // page (see login.ts's promptKeyName) — this page just relays it, same
  // as state/port. Falls back to a generic label if somehow absent (an
  // old cached page, a hand-crafted link) — the handler has its own
  // server-side default too either way.
  const name = params.get("name") ?? "CLI login";

  const [status, setStatus] = useState<
    "connecting" | "ready" | "approving" | "done" | "error"
  >("connecting");
  const [error, setError] = useState<string | null>(null);
  const [rawKey, setRawKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!state || !port) {
      setStatus("error");
      setError(
        "This link is missing required parameters — go back to your terminal and try `pure login` again.",
      );
      return;
    }
    let cancelled = false;
    ws.connect()
      .then(() => {
        if (!cancelled) setStatus("ready");
      })
      .catch(() => {
        if (cancelled) return;
        const here = window.location.pathname + window.location.search;
        window.location.href = `/login?return=${encodeURIComponent(here)}`;
      });
    return () => {
      cancelled = true;
    };
  }, [state, port]);

  useEffect(() => {
    if (!state) return;
    return ws.on("keys.createdForCli", (msg) => {
      if (msg.state !== state) return; // a different pending login in another tab
      setRawKey(msg.raw);
      setStatus("done");
      // Best-effort same-machine handoff — see this file's own header
      // comment for why the on-screen fallback below is ALWAYS shown too,
      // not conditionally on this failing (we can't detect that it did).
      window.location.href = `http://127.0.0.1:${port}/callback?state=${encodeURIComponent(state)}&key=${encodeURIComponent(msg.raw)}`;
    });
  }, [state, port]);

  // FIXED 2026-09-03 — confirmed live: clicking Authorize and hitting any
  // server-side failure (keys.ts's handleKeysMessage calls sendError on
  // every error path, never silently drops a request) left this page
  // stuck on "Authorizing…" forever — there was no listener for the
  // generic `error` message at all, so the actual reason never reached
  // the screen and approve() never had a way to let the user retry.
  useEffect(() => {
    return ws.on("error", (msg) => {
      if (msg.req_type !== "keys.createForCli") return; // not this page's request
      setStatus("error");
      setError(`Couldn't create a key: ${msg.message}`);
    });
  }, []);

  function approve() {
    if (!state) return;
    setStatus("approving");
    ws.send({ type: "keys.createForCli", state, name });
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4 bg-muted/30">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Authorize the Pure CLI</CardTitle>
          <CardDescription>
            {status === "done"
              ? "A new API key was created for this device."
              : "The Pure CLI on your machine is requesting access to your account."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === "connecting" && (
            <p className="text-sm text-muted-foreground">Connecting…</p>
          )}

          {status === "error" && error && (
            <>
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              {state && port && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setError(null);
                    setStatus("ready");
                  }}
                >
                  Try again
                </Button>
              )}
            </>
          )}

          {(status === "ready" || status === "approving") && (
            <>
              <p className="text-sm text-muted-foreground">
                This creates a new personal API key (compile access only), named{" "}
                <span className="font-medium text-foreground">{name}</span>, and
                sends it back to the terminal that opened this page.
              </p>
              <Button onClick={approve} disabled={status === "approving"}>
                {status === "approving" ? "Authorizing…" : "Authorize"}
              </Button>
            </>
          )}

          {status === "done" && rawKey && (
            <>
              <p className="text-sm text-muted-foreground">
                Your terminal should pick this up automatically. If it
                doesn&apos;t redirect (common over SSH or in a remote/container
                session), copy this key and run{" "}
                <code className="bg-muted px-1 rounded">
                  pure login --paste
                </code>{" "}
                in that terminal:
              </p>
              <div className="p-3 bg-muted rounded-md font-mono text-sm break-all">
                {rawKey}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  await navigator.clipboard.writeText(rawKey);
                  setCopied(true);
                }}
              >
                {copied ? "Copied" : "Copy to clipboard"}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
